import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Support as SupportIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as FinanceIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import api from '../../services/api';

const PaymentFailed = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  const paymentMethod = searchParams.get('method') || 'stripe';
  const reason = searchParams.get('reason');

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        // Get basic payment information for retry options
        const response = await api.get(`/v1/payments/${token}/`);
        setPaymentData(response.data);
      } catch (err) {
        setError('Failed to load payment information');
      }
    };

    if (token) {
      fetchPaymentData();
    }
  }, [token]);

  const handleRetryPayment = () => {
    // Navigate back to payment page
    navigate(`/payment/${token}`);
  };

  const getFailureMessage = () => {
    if (paymentMethod === 'payment_assist') {
      switch (reason) {
        case 'declined':
          return 'Your finance application has been declined. This could be due to credit history or other factors.';
        case 'cancelled':
          return 'You cancelled the finance application process.';
        case 'timeout':
          return 'The finance application timed out. Please try again.';
        case 'error':
          return 'There was an error processing your finance application.';
        default:
          return 'Your finance application was not successful.';
      }
    } else {
      switch (reason) {
        case 'card_declined':
          return 'Your card was declined. Please check your card details or try a different card.';
        case 'insufficient_funds':
          return 'There are insufficient funds on your card. Please use a different payment method.';
        case 'expired_card':
          return 'Your card has expired. Please use a different card.';
        case 'incorrect_cvc':
          return 'The security code (CVC) is incorrect. Please check and try again.';
        case 'processing_error':
          return 'There was an error processing your payment. Please try again.';
        default:
          return 'Your payment was not successful.';
      }
    }
  };

  const getRetryOptions = () => {
    if (paymentMethod === 'payment_assist') {
      return [
        {
          icon: <CreditCardIcon />,
          title: 'Try Card Payment Instead',
          description: 'Pay immediately with your credit or debit card',
          action: 'card'
        },
        {
          icon: <FinanceIcon />,
          title: 'Try Finance Again',
          description: 'Re-apply for finance with different details',
          action: 'finance'
        }
      ];
    } else {
      return [
        {
          icon: <CreditCardIcon />,
          title: 'Try Different Card',
          description: 'Use a different credit or debit card',
          action: 'card'
        },
        {
          icon: <FinanceIcon />,
          title: 'Finance Option Available',
          description: 'Spread the cost with 0% APR finance',
          action: 'finance'
        }
      ];
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Failed Header */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h3" color="error.main" gutterBottom>
            Payment Failed
          </Typography>
          <Typography variant="h6" color="text.secondary">
            We couldn't process your payment
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Failure Reason */}
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            {paymentMethod === 'payment_assist' ? 'Finance Application Failed' : 'Payment Failed'}
          </Typography>
          <Typography variant="body2">
            {getFailureMessage()}
          </Typography>
        </Alert>

        {/* Payment Summary */}
        {paymentData && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Customer:</strong> {paymentData.customer.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Vehicle:</strong> {paymentData.booking.vehicle.make} {paymentData.booking.vehicle.model} ({paymentData.booking.vehicle.registration})
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Amount:</strong> {formatCurrency(paymentData.amount)}
              </Typography>
              <Typography variant="body2">
                <strong>Payment Method:</strong> {paymentMethod === 'payment_assist' ? 'Finance' : 'Card Payment'}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Retry Options */}
        <Typography variant="h6" gutterBottom>
          Try Another Payment Method
        </Typography>

        <Box sx={{ mb: 3 }}>
          {getRetryOptions().map((option, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                  borderColor: 'primary.main'
                }
              }}
              onClick={handleRetryPayment}
            >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Box sx={{ mr: 2, color: 'primary.main' }}>
                    {option.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Common Issues & Solutions */}
        <Typography variant="h6" gutterBottom>
          Common Issues & Solutions
        </Typography>

        <List>
          {paymentMethod === 'payment_assist' ? (
            <>
              <ListItem>
                <ListItemIcon>
                  <FinanceIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Credit Check Requirements"
                  secondary="Ensure your details match your credit file exactly"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CreditCardIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Try Card Payment"
                  secondary="Card payments are processed instantly without credit checks"
                />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem>
                <ListItemIcon>
                  <CreditCardIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Check Card Details"
                  secondary="Verify card number, expiry date, and security code"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FinanceIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Consider Finance"
                  secondary="Spread the cost with our 0% APR finance option"
                />
              </ListItem>
            </>
          )}
        </List>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleRetryPayment}
            startIcon={<RefreshIcon />}
            sx={{ mr: 2 }}
          >
            Try Again
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Box>

        {/* Support Information */}
        <Card sx={{ mt: 4, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
          <CardContent>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <SupportIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight="bold" color="warning.main">
                Need Help?
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2 }}>
              If you continue to experience issues with payment, our support team is here to help.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<PhoneIcon />}
                label="Call Support"
                variant="outlined"
                clickable
              />
              <Chip
                icon={<EmailIcon />}
                label="Email Support"
                variant="outlined"
                clickable
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Reference: {token}
            </Typography>
          </CardContent>
        </Card>

        {/* Important Notice */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Your vehicle service is currently on hold pending payment.
            Once payment is successful, work will resume immediately.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default PaymentFailed;